///
/// EarthBrowser
///
/// City Weather Label Styling
///
/// Shared visual vocabulary for city weather labels and the location hover
/// callout: a diverging (colorblind-safe) temperature ramp, vector-drawn
/// weather icons with day/night variants, and small glyphs for wind
/// direction, pressure trend, and aurora chance.
///
/// Created by Matt Giger on September 5, 2026
/// Copyright (c) 2026 Geodata Labs
///

type Ctx2D = OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D

// ============================================================================
// Temperature color ramp
// ============================================================================

/**
 * Diverging cool/warm ramp (ColorBrewer RdYlBu hues, reversed), anchored
 * to how temperatures are felt rather than spread evenly over -40..+50°C.
 *
 * The hues are kept because they are colorblind-safe and contain no green
 * (labels sit over green terrain). The anchors put the pale neutral at
 * ~14°C (mild), so a 5°C morning reads light blue and 30°C reads orange;
 * an evenly spaced ramp had its neutral at freezing, which painted a
 * 3°C (38°F) chip cream and a 20°C (68°F) one hot orange. Most of the
 * ramp's resolution is spent on -10..35°C, where people live.
 */
const TEMP_STOPS: Array<[number, number, number, number]> = [
	[-35, 49, 54, 149],
	[-20, 69, 117, 180],
	[-10, 116, 173, 209],
	[0, 171, 217, 233],
	[8, 212, 233, 243],
	[14, 242, 243, 228],
	[20, 254, 228, 150],
	[26, 253, 180, 100],
	[31, 244, 115, 70],
	[37, 215, 48, 39],
	[45, 165, 0, 38]
]

/** At or below freezing: the chip gets an icy outline instead of the dark hairline */
export function isFreezing(tempC: number): boolean {
	return tempC <= 0
}

/**
 * Interpolated [r, g, b] for a temperature in °C
 */
export function getTempRgb(tempC: number): [number, number, number] {
	const first = TEMP_STOPS[0]
	if (tempC <= first[0]) return [first[1], first[2], first[3]]

	for (let i = 1; i < TEMP_STOPS.length; i++) {
		const [t1, r1, g1, b1] = TEMP_STOPS[i]
		if (tempC <= t1) {
			const [t0, r0, g0, b0] = TEMP_STOPS[i - 1]
			const t = (tempC - t0) / (t1 - t0)
			return [
				Math.round(r0 + (r1 - r0) * t),
				Math.round(g0 + (g1 - g0) * t),
				Math.round(b0 + (b1 - b0) * t)
			]
		}
	}

	const last = TEMP_STOPS[TEMP_STOPS.length - 1]
	return [last[1], last[2], last[3]]
}

/**
 * CSS color for a temperature in °C
 */
export function getTempColor(tempC: number, alpha: number = 1): string {
	const [r, g, b] = getTempRgb(tempC)
	return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

/**
 * Text color that stays legible on a chip filled with the temp color:
 * dark navy on the pale mid-range, white on the saturated extremes
 */
export function getTempTextColor(tempC: number, alpha: number = 1): string {
	const [r, g, b] = getTempRgb(tempC)
	const luminance = 0.299 * r + 0.587 * g + 0.114 * b
	return luminance > 150 ? `rgba(19, 34, 56, ${alpha})` : `rgba(255, 255, 255, ${alpha})`
}

// ============================================================================
// Icon codes
// ============================================================================

export type WeatherIconCode =
	| 'clear'
	| 'mostly_clear'
	| 'partly_cloudy'
	| 'mostly_cloudy'
	| 'cloudy'
	| 'drizzle'
	| 'light_rain'
	| 'rain'
	| 'heavy_rain'
	| 'snow'
	| 'sleet'
	| 'thunderstorm'
	| 'unknown'

/**
 * Map a grid weather code to a drawable icon code. Snow and thunderstorm
 * codes come from the grid's precipitation type / CAPE; the temperature
 * fallback still catches grids published without a precipitation type.
 */
export function resolveIconCode(weatherCode: string | null, tempC: number | null): WeatherIconCode {
	if (!weatherCode) return 'unknown'

	const isPrecip = weatherCode === 'drizzle' || weatherCode === 'light_rain' ||
		weatherCode === 'rain' || weatherCode === 'heavy_rain'
	if (isPrecip && tempC !== null && tempC <= 0.5) return 'snow'

	switch (weatherCode) {
		case 'clear':
		case 'mostly_clear':
		case 'partly_cloudy':
		case 'mostly_cloudy':
		case 'cloudy':
		case 'drizzle':
		case 'light_rain':
		case 'rain':
		case 'heavy_rain':
		case 'snow':
		case 'sleet':
		case 'thunderstorm':
			return weatherCode
		case 'heavy_snow':
			return 'snow'
		case 'freezing_rain':
			return 'sleet'
		default:
			return 'unknown'
	}
}

/**
 * Weather worth showing on a label that otherwise stays plain (small
 * towns): anything falling from the sky, or a storm. Clear and cloudy are
 * the background state and don't earn an icon at that tier.
 */
export function isSignificantWeather(code: WeatherIconCode): boolean {
	switch (code) {
		case 'drizzle':
		case 'light_rain':
		case 'rain':
		case 'heavy_rain':
		case 'snow':
		case 'sleet':
		case 'thunderstorm':
			return true
		default:
			return false
	}
}

// ============================================================================
// Icon drawing
// ============================================================================

const SUN_COLOR = '#ffd166'
const MOON_COLOR = '#f5e9c8'
const CLOUD_COLOR = '#e6edf4'
const CLOUD_DARK_COLOR = '#c3ceda'
const RAIN_COLOR = '#8ecbff'
const SNOW_COLOR = '#eaf3ff'
const ICON_OUTLINE = 'rgba(0, 0, 0, 0.55)'

function drawSun(ctx: Ctx2D, cx: number, cy: number, r: number): void {
	const rayInner = r * 1.3
	const rayOuter = r * 1.75

	ctx.strokeStyle = SUN_COLOR
	ctx.lineWidth = Math.max(1, r * 0.32)
	ctx.lineCap = 'round'
	ctx.beginPath()
	for (let i = 0; i < 8; i++) {
		const a = (i / 8) * Math.PI * 2
		ctx.moveTo(cx + Math.cos(a) * rayInner, cy + Math.sin(a) * rayInner)
		ctx.lineTo(cx + Math.cos(a) * rayOuter, cy + Math.sin(a) * rayOuter)
	}
	ctx.stroke()

	ctx.beginPath()
	ctx.arc(cx, cy, r, 0, Math.PI * 2)
	ctx.fillStyle = SUN_COLOR
	ctx.fill()
	ctx.strokeStyle = ICON_OUTLINE
	ctx.lineWidth = 1
	ctx.stroke()
}

function drawMoon(ctx: Ctx2D, cx: number, cy: number, r: number): void {
	// Crescent: outer arc of the moon disc, closed by an inner arc offset
	// toward the lit side
	ctx.beginPath()
	ctx.arc(cx, cy, r, Math.PI * 0.3, Math.PI * 1.55, false)
	ctx.arc(cx + r * 0.55, cy - r * 0.25, r * 0.8, Math.PI * 1.4, Math.PI * 0.45, true)
	ctx.closePath()
	ctx.fillStyle = MOON_COLOR
	ctx.fill()
	ctx.strokeStyle = ICON_OUTLINE
	ctx.lineWidth = 1
	ctx.stroke()
}

function cloudPath(ctx: Ctx2D, cx: number, cy: number, w: number): void {
	// Puffy tri-lobe cloud with a flat base, centered on (cx, cy)
	const baseY = cy + w * 0.18
	ctx.beginPath()
	ctx.arc(cx - w * 0.24, baseY - w * 0.14, w * 0.22, Math.PI * 0.5, Math.PI * 1.5)
	ctx.arc(cx + w * 0.02, baseY - w * 0.26, w * 0.28, Math.PI * 0.95, Math.PI * 1.92)
	ctx.arc(cx + w * 0.27, baseY - w * 0.13, w * 0.2, Math.PI * 1.35, Math.PI * 0.5)
	ctx.closePath()
}

function drawCloud(ctx: Ctx2D, cx: number, cy: number, w: number, fill: string = CLOUD_COLOR): void {
	cloudPath(ctx, cx, cy, w)
	ctx.fillStyle = fill
	ctx.fill()
	ctx.strokeStyle = ICON_OUTLINE
	ctx.lineWidth = 1
	ctx.stroke()
}

function drawDrops(ctx: Ctx2D, cx: number, y: number, size: number, count: number, length: number): void {
	ctx.strokeStyle = RAIN_COLOR
	ctx.lineWidth = Math.max(1, size * 0.09)
	ctx.lineCap = 'round'
	ctx.beginPath()
	const spread = size * 0.24
	const startX = cx - ((count - 1) / 2) * spread
	for (let i = 0; i < count; i++) {
		const x = startX + i * spread
		ctx.moveTo(x, y)
		ctx.lineTo(x - length * 0.35, y + length)
	}
	ctx.stroke()
}

function drawBolt(ctx: Ctx2D, cx: number, y: number, size: number): void {
	const s = size * 0.32
	ctx.beginPath()
	ctx.moveTo(cx + s * 0.25, y)
	ctx.lineTo(cx - s * 0.45, y + s * 0.75)
	ctx.lineTo(cx - s * 0.05, y + s * 0.75)
	ctx.lineTo(cx - s * 0.3, y + s * 1.5)
	ctx.lineTo(cx + s * 0.5, y + s * 0.55)
	ctx.lineTo(cx + s * 0.08, y + s * 0.55)
	ctx.lineTo(cx + s * 0.6, y)
	ctx.closePath()
	ctx.fillStyle = SUN_COLOR
	ctx.fill()
}

function drawFlakes(ctx: Ctx2D, cx: number, y: number, size: number, count: number): void {
	ctx.strokeStyle = SNOW_COLOR
	ctx.lineWidth = Math.max(1, size * 0.07)
	ctx.lineCap = 'round'
	ctx.beginPath()
	const spread = size * 0.26
	const startX = cx - ((count - 1) / 2) * spread
	const r = size * 0.09
	for (let i = 0; i < count; i++) {
		const x = startX + i * spread
		const fy = y + (i % 2 === 0 ? 0 : size * 0.12) + r
		for (let a = 0; a < 3; a++) {
			const ang = (a / 3) * Math.PI
			ctx.moveTo(x - Math.cos(ang) * r, fy - Math.sin(ang) * r)
			ctx.lineTo(x + Math.cos(ang) * r, fy + Math.sin(ang) * r)
		}
	}
	ctx.stroke()
}

/**
 * Draw a weather condition icon centered at (cx, cy) in a box of `size` px.
 * `night` swaps sun glyphs for moon glyphs (clear/partly conditions).
 */
export function drawWeatherIcon(
	ctx: Ctx2D,
	code: WeatherIconCode,
	night: boolean,
	cx: number,
	cy: number,
	size: number,
	alpha: number = 1
): void {
	if (code === 'unknown') return

	ctx.save()
	ctx.globalAlpha *= alpha

	const luminary = (x: number, y: number, r: number) => {
		if (night) drawMoon(ctx, x, y, r)
		else drawSun(ctx, x, y, r)
	}

	switch (code) {
		case 'clear':
			luminary(cx, cy, size * (night ? 0.38 : 0.28))
			break

		case 'mostly_clear':
			luminary(cx - size * 0.05, cy - size * 0.05, size * (night ? 0.34 : 0.26))
			drawCloud(ctx, cx + size * 0.2, cy + size * 0.24, size * 0.5)
			break

		case 'partly_cloudy':
			luminary(cx + size * 0.16, cy - size * 0.18, size * (night ? 0.28 : 0.22))
			drawCloud(ctx, cx - size * 0.04, cy + size * 0.12, size * 0.78)
			break

		case 'mostly_cloudy':
			luminary(cx + size * 0.26, cy - size * 0.22, size * (night ? 0.22 : 0.17))
			drawCloud(ctx, cx - size * 0.02, cy + size * 0.08, size * 0.9, CLOUD_DARK_COLOR)
			break

		case 'cloudy':
			drawCloud(ctx, cx, cy, size * 0.95, CLOUD_DARK_COLOR)
			break

		case 'drizzle':
			drawCloud(ctx, cx, cy - size * 0.14, size * 0.82)
			drawDrops(ctx, cx, cy + size * 0.18, size, 2, size * 0.16)
			break

		case 'light_rain':
			drawCloud(ctx, cx, cy - size * 0.14, size * 0.82)
			drawDrops(ctx, cx, cy + size * 0.16, size, 2, size * 0.26)
			break

		case 'rain':
			drawCloud(ctx, cx, cy - size * 0.14, size * 0.85, CLOUD_DARK_COLOR)
			drawDrops(ctx, cx, cy + size * 0.16, size, 3, size * 0.28)
			break

		case 'heavy_rain':
			drawCloud(ctx, cx, cy - size * 0.16, size * 0.9, CLOUD_DARK_COLOR)
			drawDrops(ctx, cx - size * 0.22, cy + size * 0.14, size, 2, size * 0.26)
			drawBolt(ctx, cx + size * 0.16, cy + size * 0.1, size)
			break

		case 'snow':
			drawCloud(ctx, cx, cy - size * 0.16, size * 0.82)
			drawFlakes(ctx, cx, cy + size * 0.14, size, 3)
			break

		case 'sleet':
			drawCloud(ctx, cx, cy - size * 0.16, size * 0.82)
			drawDrops(ctx, cx - size * 0.16, cy + size * 0.16, size, 1, size * 0.24)
			drawFlakes(ctx, cx + size * 0.14, cy + size * 0.14, size, 1)
			break

		case 'thunderstorm':
			drawCloud(ctx, cx, cy - size * 0.16, size * 0.9, CLOUD_DARK_COLOR)
			drawBolt(ctx, cx, cy + size * 0.08, size * 1.2)
			break
	}

	ctx.restore()
}

// ============================================================================
// Auxiliary glyphs
// ============================================================================

/**
 * Wind direction arrow. `windFromDeg` is the meteorological direction the
 * wind comes FROM; the arrow points where the wind is blowing TOWARD,
 * with screen-up = north.
 */
export function drawWindArrow(
	ctx: Ctx2D,
	cx: number,
	cy: number,
	size: number,
	windFromDeg: number,
	alpha: number = 1
): void {
	ctx.save()
	ctx.globalAlpha *= alpha
	ctx.translate(cx, cy)
	ctx.rotate(((windFromDeg + 180) * Math.PI) / 180)

	const half = size * 0.42
	const headW = size * 0.26

	// Dark halo for contrast over terrain
	ctx.strokeStyle = 'rgba(0, 0, 0, 0.6)'
	ctx.lineWidth = Math.max(2.5, size * 0.28)
	ctx.lineCap = 'round'
	ctx.beginPath()
	ctx.moveTo(0, half)
	ctx.lineTo(0, -half * 0.4)
	ctx.stroke()

	ctx.strokeStyle = '#cfe6ff'
	ctx.lineWidth = Math.max(1.2, size * 0.12)
	ctx.beginPath()
	ctx.moveTo(0, half)
	ctx.lineTo(0, -half * 0.4)
	ctx.stroke()

	ctx.beginPath()
	ctx.moveTo(0, -half)
	ctx.lineTo(-headW, -half + headW * 1.5)
	ctx.lineTo(headW, -half + headW * 1.5)
	ctx.closePath()
	ctx.fillStyle = '#cfe6ff'
	ctx.fill()
	ctx.strokeStyle = 'rgba(0, 0, 0, 0.6)'
	ctx.lineWidth = 1
	ctx.stroke()

	ctx.restore()
}

/**
 * Pressure trend chevron: up = rising (fair), down = falling (unsettled,
 * drawn in amber since falling pressure is the interesting signal)
 */
export function drawTrendChevron(
	ctx: Ctx2D,
	cx: number,
	cy: number,
	size: number,
	rising: boolean,
	alpha: number = 1
): void {
	ctx.save()
	ctx.globalAlpha *= alpha

	const w = size * 0.4
	const h = size * 0.26
	const dir = rising ? -1 : 1

	const chevron = (offsetY: number) => {
		ctx.beginPath()
		ctx.moveTo(cx - w, cy + offsetY + h * -dir)
		ctx.lineTo(cx, cy + offsetY + h * dir)
		ctx.lineTo(cx + w, cy + offsetY + h * -dir)
		ctx.stroke()
	}

	ctx.lineCap = 'round'
	ctx.lineJoin = 'round'
	ctx.strokeStyle = 'rgba(0, 0, 0, 0.6)'
	ctx.lineWidth = Math.max(2.5, size * 0.3)
	chevron(0)

	ctx.strokeStyle = rising ? 'rgba(214, 230, 244, 0.95)' : '#fbbf24'
	ctx.lineWidth = Math.max(1.2, size * 0.14)
	chevron(0)

	ctx.restore()
}

/**
 * Aurora chance badge: a small green curtain arc
 */
export function drawAuroraArc(
	ctx: Ctx2D,
	cx: number,
	cy: number,
	size: number,
	alpha: number = 1
): void {
	ctx.save()
	ctx.globalAlpha *= alpha
	ctx.lineCap = 'round'

	ctx.strokeStyle = 'rgba(0, 0, 0, 0.6)'
	ctx.lineWidth = Math.max(3, size * 0.34)
	ctx.beginPath()
	ctx.arc(cx, cy + size * 0.3, size * 0.44, Math.PI * 1.2, Math.PI * 1.8)
	ctx.stroke()

	ctx.strokeStyle = '#4ade80'
	ctx.lineWidth = Math.max(1.5, size * 0.16)
	ctx.beginPath()
	ctx.arc(cx, cy + size * 0.3, size * 0.44, Math.PI * 1.2, Math.PI * 1.8)
	ctx.stroke()

	ctx.strokeStyle = 'rgba(74, 222, 128, 0.55)'
	ctx.lineWidth = Math.max(1, size * 0.1)
	ctx.beginPath()
	ctx.arc(cx, cy + size * 0.44, size * 0.32, Math.PI * 1.25, Math.PI * 1.75)
	ctx.stroke()

	ctx.restore()
}

/**
 * Compass cardinal for a wind "from" direction in degrees
 */
export function cardinalFromDeg(deg: number): string {
	const cardinals = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW']
	return cardinals[Math.round(deg / 45) % 8]
}
